import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout from '@components/Layout';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/store';
import { MINPAGESIZE3 } from 'global/Global';
import { updatePageLoadingApi } from '@slice/select.slice';
import { useTranslation } from 'react-i18next';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { Button, DatePicker, Select, message } from 'antd';
import Helpers from 'helpers/Helpers';
import Table, { IColumnTable } from '@components/table-devextreme/Table';
import TableTitle from '@components/table-devextreme/TableTitle';
import moment from 'moment';
import EditButton from '@components/table-devextreme/buttons/EditButton';
import DeleteButton from '@components/table-devextreme/buttons/DeleteButton';
import { useAdminConfig } from '@pages/OrganizedSales/CreateOrders/common/Hooks/XuLyDonNo';
import DataManagementApi from 'apis/api/DataManagement';
import ImportFileModal from './Modify/ImportFileModal';
import UpdatePriceReturn from './Modify/UpdatePriceReturn';
import DeleteReturnProduct from './Modify/DeleteReturnProduct';

import viVN from 'antd/es/locale/vi_VN';
import 'moment/locale/vi';

/* ========================================================================== */
/* Constants & pure helpers (nằm ngoài component để không bị tạo lại)         */
/* ========================================================================== */

const GROUP_FIELD = '_sortKey';
const DATE_FMT = 'DD/MM/YYYY';
const DATETIME_FMT = 'DD/MM/YYYY HH:mm:ss';

const isValidDate = (d?: string) => !!d && !d.startsWith('0001-01-01');
const fmtDate = (d?: string, fmt = DATE_FMT) => (isValidDate(d) ? moment(d).format(fmt) : '');
const pad = (v: any) => String(v ?? '').padStart(12, '0');

/**
 * Mỗi phiếu chỉ có 1 ngày / 1 PN / 1 ngày import, nên "sort toàn bộ theo cột X"
 * thực chất là "sort các nhóm (phiếu) theo X".
 * Khi bấm sort các cột bên dưới, handler sẽ đổi cách tính giá trị group của cột _sortKey.
 */
// Khoá phiếu: pad để sort theo số, kèm PN gốc để "00000001", "01", "1" không bị gom chung 1 phiếu
// (pad cả 3 đều ra "000000000001")
const voucherKey = (row: any) => `${pad(row.voucherNo)}-${row.voucherNo ?? ''}`;

const GROUP_VALUE_BY_FIELD: Record<string, (row: any) => string> = {
  returnDateExport: row => `${row._ymd || '00000000'}-${voucherKey(row)}`,
  voucherNo: row => voucherKey(row),
  lastImportDateExport: row => `${pad(row._importTs)}-${voucherKey(row)}`
};
const DEFAULT_GROUP_VALUE = GROUP_VALUE_BY_FIELD.returnDateExport;

const mapVouchersToRows = (vouchers: any[]): any[] => {
  const rows: any[] = [];

  vouchers.forEach((voucher: any) => {
    (voucher.items || []).forEach((it: any) => {
      const unitPrice = it.unitPrice || 0;
      const amount = it.amount || unitPrice * (it.quantity || 0);

      rows.push({
        ...it,
        key: `${voucher.voucherNo}-${it.lineNo}-${it.id}`,
        batchId: voucher.id,
        voucherNo: voucher.voucherNo,
        returnDate: voucher.returnDate,
        isCompleted: voucher.isCompleted,
        voucherTotalAmount: voucher.totalAmount || 0,
        unitPrice,
        amount,

        // Trường dùng để group / sort
        [GROUP_FIELD]: `${voucher.voucherNo || ''}`,
        _ymd: isValidDate(voucher.returnDate) ? moment(voucher.returnDate).format('YYYYMMDD') : '',
        _importTs: isValidDate(voucher.lastImportDate) ? new Date(voucher.lastImportDate).getTime() : 0,

        // Trường hiển thị / export
        returnDateExport: fmtDate(voucher.returnDate),
        lastImportDateExport: fmtDate(voucher.lastImportDate, DATETIME_FMT),
        updateDateExport: fmtDate(it.updateDate, DATETIME_FMT),
        amountExport: Helpers.FormatMoney(amount, true),
        statusExport: unitPrice > 0 ? 'Đã có đơn giá' : 'Chưa có đơn giá',
        updateUserExport: it.updateUser || ''
      });
    });
  });

  // Sắp xếp ban đầu: phiếu mới nhất trước, trong phiếu theo số dòng
  return rows.sort((a, b) => {
    const c = String(b[GROUP_FIELD]).localeCompare(String(a[GROUP_FIELD]));
    return c !== 0 ? c : (a.lineNo || 0) - (b.lineNo || 0);
  });
};

const filterBatchesByMonth = (list: any[], year: number, month: number) =>
  list.filter((b: any) => b.year === year && b.month === month);

const StatusPriceBadge = ({ data }: { data: any }) => {
  const ok = (data.unitPrice || 0) > 0;
  return (
    <span
      style={{
        background: ok ? 'green' : 'red',
        color: 'white',
        fontWeight: 300,
        padding: '2px 6px',
        width: 'fit-content',
        borderRadius: '2px'
      }}
    >
      {data.statusExport}
    </span>
  );
};

/* ========================================================================== */
/* Component                                                                  */
/* ========================================================================== */

export default function ReturnProducts() {
  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const uLogin = useSelector((state: RootState) => state.user.account);
  const { newValue } = useAdminConfig('UseManageReturnProduct');

  // Modal state
  const [isUpdate, setIsUpdate] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [openImportXls, setOpenImportXls] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any>(null);

  // Data state
  const [dataTable, setDataTable] = useState<any[]>([]);
  const [allBatches, setAllBatches] = useState<any[]>([]);
  const [initialLoaded, setInitialLoaded] = useState(false);

  // batchId: batch ĐANG hiển thị | pendingBatchId: batch đang CHỌN trên Select
  const [batchId, setBatchId] = useState<number | undefined>(undefined);
  const [pendingBatchId, setPendingBatchId] = useState<number | undefined>(undefined);

  const [query, setQuery] = useState({ year: moment().year(), month: moment().month() + 1 });

  const didInitRef = useRef(false);

  // Root/Admin luôn được import, còn lại chỉ tài khoản có trong cấu hình
  const checkAuth = useMemo(
    () =>
      ['Root', 'Admin'].includes(uLogin?.role) ||
      (!!uLogin?.username && (newValue || []).some((u: string) => u.trim() === uLogin.username)),
    [uLogin?.role, uLogin?.username, newValue]
  );

  // Danh sách file của tháng/năm đang chọn - lọc thuần client
  const batches = useMemo(
    () => filterBatchesByMonth(allBatches, query.year, query.month),
    [allBatches, query.year, query.month]
  );

  /* ---------------------------------------------------------------------- */
  /* API                                                                    */
  /* ---------------------------------------------------------------------- */

  const fetchItemByBatch = useCallback(
    async (id?: number) => {
      if (!id) {
        setDataTable([]);
        return;
      }
      try {
        const res = await DataManagementApi.GetItemByBatch(id);
        if (res.data?.error) {
          message.error(res.data?.message || t('System.FetchError'));
          setDataTable([]);
          return;
        }
        setDataTable(mapVouchersToRows(res.data?.data || []));
      } catch (error) {
        console.log(error);
        message.error(t('System.FetchError'));
        setDataTable([]);
      }
    },
    [t]
  );

  /** Fetch + bật/tắt loading toàn trang */
  const fetchItemWithLoading = useCallback(
    async (id?: number) => {
      dispatch(updatePageLoadingApi(true));
      try {
        await fetchItemByBatch(id);
      } finally {
        dispatch(updatePageLoadingApi(false));
      }
    },
    [dispatch, fetchItemByBatch]
  );

  const fetchAllBatches = useCallback(async (): Promise<any[]> => {
    dispatch(updatePageLoadingApi(true));
    try {
      const res = await DataManagementApi.GetBatch();
      if (res.data?.error) {
        message.error(res.data?.message || t('System.FetchError'));
        setAllBatches([]);
        return [];
      }
      const list = res.data?.data || [];
      setAllBatches(list);
      return list;
    } catch (error) {
      console.log(error);
      message.error(t('System.FetchError'));
      setAllBatches([]);
      return [];
    } finally {
      dispatch(updatePageLoadingApi(false));
    }
  }, [dispatch, t]);

  /* ---------------------------------------------------------------------- */
  /* Effects                                                                */
  /* ---------------------------------------------------------------------- */

  // Tải danh sách batch đúng 1 lần khi vào trang
  useEffect(() => {
    fetchAllBatches().finally(() => setInitialLoaded(true));
  }, []);

  // Đổi tháng / allBatches đổi -> cập nhật lựa chọn mặc định.
  // Chỉ tự load data đúng 1 lần (lần đầu), các lần sau phải bấm "Xem".
  useEffect(() => {
    if (!initialLoaded) return;

    const defaultId = batches.some((b: any) => b.id === pendingBatchId) ? pendingBatchId : batches[0]?.id;
    setPendingBatchId(defaultId);

    if (!didInitRef.current) {
      didInitRef.current = true;
      setBatchId(defaultId);
      fetchItemByBatch(defaultId);
    }
  }, [initialLoaded, batches]);

  /* ---------------------------------------------------------------------- */
  /* Handlers                                                               */
  /* ---------------------------------------------------------------------- */

  const onClickViewBatch = useCallback(async () => {
    setBatchId(pendingBatchId);
    await fetchItemWithLoading(pendingBatchId);
  }, [pendingBatchId, fetchItemWithLoading]);

  // Sau import / sửa / xóa -> refresh toàn bộ và apply luôn
  const callBackRefresh = useCallback(async () => {
    const list = await fetchAllBatches();
    const filtered = filterBatchesByMonth(list, query.year, query.month);
    const nextId = filtered.some((b: any) => b.id === batchId) ? batchId : filtered[0]?.id;

    setPendingBatchId(nextId);
    setBatchId(nextId);
    await fetchItemWithLoading(nextId);
  }, [fetchAllBatches, fetchItemWithLoading, query.year, query.month, batchId]);

  const handleEdit = useCallback((data: any) => {
    setSelectedRows(data);
    setIsUpdate(true);
  }, []);

  const handleDelete = useCallback((data: any) => {
    setSelectedRows(data);
    setIsDelete(true);
  }, []);

  /**
   * Bảng đang group theo phiếu nên DevExtreme luôn sort theo group trước.
   * Khi bấm sort Ngày nhập / PN / Ngày import, ta đổi cách tính giá trị group
   * và đồng bộ chiều sort sang cột group -> sort được toàn bộ các phiếu.
   */
  const onGridInitialized = useCallback((e: any) => {
    const grid = e.component;

    // Nhớ trạng thái hiện tại để bỏ qua các thay đổi thừa
    let groupFn = DEFAULT_GROUP_VALUE;
    let groupOrder = 'desc';
    let sortedField: string | undefined; // cột đang sort (chỉ giữ 1 cột)
    let busy = false; // chặn handler chạy lại do chính nó gây ra

    grid.on('optionChanged', (args: any) => {
      if (busy) return;

      const m = /^(columns\[\d+\](?:\.columns\[\d+\])*)\.sortOrder$/.exec(args.fullName || '');
      if (!m) return;

      const field = grid.option(`${m[1]}.dataField`);
      if (!field || field === GROUP_FIELD) return;

      const isGroupSortField = !!GROUP_VALUE_BY_FIELD[field];

      // Bỏ sort (bấm lần thứ 3) ở đúng cột đang sort -> về mặc định
      // Giá trị rỗng ở cột khác (bị bỏ do sort cột mới) -> bỏ qua
      if (!args.value && field !== sortedField) return;

      const nextOrder = args.value && isGroupSortField ? args.value : 'desc';
      const nextFn = args.value && isGroupSortField ? GROUP_VALUE_BY_FIELD[field] : DEFAULT_GROUP_VALUE;
      const prevField = sortedField;
      sortedField = args.value ? field : undefined;

      const needClear = !!prevField && prevField !== field;
      const fnChanged = nextFn !== groupFn;
      const orderChanged = nextOrder !== groupOrder;

      // Không có gì cần đổi -> không đụng vào grid (đây là phần giúp hết giật)
      if (!needClear && !fnChanged && !orderChanged) return;

      busy = true;
      grid.beginUpdate();
      try {
        // Chỉ xoá sort của đúng cột trước đó, không quét toàn bộ cột
        if (needClear) grid.columnOption(prevField, 'sortOrder', undefined);
        if (fnChanged) {
          grid.columnOption(GROUP_FIELD, 'calculateGroupValue', nextFn);
          groupFn = nextFn;
        }
        if (orderChanged) {
          grid.columnOption(GROUP_FIELD, 'sortOrder', nextOrder);
          groupOrder = nextOrder;
        }
      } finally {
        grid.endUpdate();
        busy = false;
      }
    });
  }, []);

  const formatCustomSum = useCallback((value: any) => Helpers.FormatMoney(value, true), []);

  /* ---------------------------------------------------------------------- */
  /* Columns (đúng thứ tự cột A -> R của file Excel)                        */
  /* ---------------------------------------------------------------------- */

  const columns: IColumnTable[] = useMemo(
    () => [
      {
        dataField: GROUP_FIELD,
        caption: 'Phiếu nhập',
        visible: false,
        showInColumnChooser: false,
        groupIndex: 0,
        sortOrder: 'desc',
        calculateGroupValue: DEFAULT_GROUP_VALUE,
        calculateSortValue: DEFAULT_GROUP_VALUE,
        groupCellRender: (cellInfo: any) => {
          // Nhóm đang thu gọn thì DevExtreme để items = null, các dòng nằm ở collapsedItems
          const row = (cellInfo.data?.items || cellInfo.data?.collapsedItems || [])[0];
          const voucher = row?.voucherNo || 'N/A';
          const date = row?.returnDateExport;
          const total = Helpers.FormatMoney(row?.voucherTotalAmount || 0, true);
          return (
            <span>
              Số phiếu: {voucher}
              {date ? ` - Ngày nhập: ${date}` : ''} - Tổng tiền: {total}
            </span>
          );
        }
      } as any,
      {
        type: 'buttons',
        dataField: 'Function',
        alignment: 'center',
        width: 70,
        caption: '',
        allowFiltering: false,
        allowSorting: false,
        showInColumnChooser: false,
        fixed: true,
        cellRender: (props: any) => (
          <div className="flex space-x-2 items-center justify-center">
            <EditButton onClick={() => handleEdit(props.data)} />
            <DeleteButton onClick={() => handleDelete(props.data)} />
          </div>
        )
      },
      {
        dataField: 'lineNo',
        caption: 'Dòng',
        width: 70,
        alignment: 'center',
        allowFiltering: false,
        fixed: true,
        cssClass: 'font-semibold text-green-600'
      } as any,
      {
        dataField: 'returnDateExport', // A - NGÀY
        caption: 'Ngày nhập',
        dataType: 'string',
        width: 110,
        fixed: true,
        calculateSortValue: (row: any) => row._ymd
      } as any,
      { dataField: 'voucherNo', caption: 'PN', width: 100, dataType: 'string' }, // B
      {
        caption: 'Hàng Hoá',
        childHeader: [
          { dataField: 'productName', caption: 'Tên Hàng', width: 200, dataType: 'string' }, // C
          { dataField: 'unit', caption: 'ĐV Tính', width: 80, alignment: 'center', dataType: 'string' }, // D
          { dataField: 'quantity', caption: 'SL Nhập', width: 90, alignment: 'right', format: 'fixedPoint' }, // E
          {
            dataField: 'unitPrice', // F
            caption: 'Đơn Giá',
            width: 120,
            alignment: 'right',
            format: 'fixedPoint',
            cssClass: 'font-semibold text-blue-600'
          } as any,
          {
            dataField: 'amount', // G
            caption: 'Thành Tiền',
            width: 140,
            alignment: 'right',
            format: 'fixedPoint',
            sumCol: 'amount',
            formatCustomSum,
            isSumFooter: true,
            cssClass: 'font-semibold text-red-500'
          } as any
        ]
      },
      { dataField: 'saleAccount', caption: 'Kinh Doanh', width: 120, dataType: 'string' }, // H
      {
        caption: 'Khách Hàng',
        childHeader: [
          { dataField: 'customerCode', caption: 'Mã KH', width: 140, dataType: 'string' }, // I
          { dataField: 'customerName', caption: 'Tên KH', width: 150, dataType: 'string' },
          { dataField: 'customerPhoneNum', caption: 'SĐT KH', width: 110, dataType: 'string' },
          { dataField: 'distributorName', caption: 'Đại Lý', width: 150, dataType: 'string' }, // J
          { dataField: 'orderCode', caption: 'Mã Đơn', width: 130, dataType: 'string' } // K
        ]
      },
      { dataField: 'note', caption: 'Ghi Chú', width: 250, dataType: 'string' }, // L
      {
        caption: 'Serial',
        childHeader: [
          { dataField: 'serial', caption: 'Serial Nhận', width: 130, dataType: 'string' }, // M
          { dataField: 'carNo', caption: 'Số Xe', width: 100, dataType: 'string' }, // N
          { dataField: 'simSerial', caption: 'Serial Sim', width: 130, dataType: 'string' }, // O
          { dataField: 'simNo', caption: 'Số Sim', width: 120, dataType: 'string' } // P
        ]
      },
      // { dataField: 'status', caption: 'Tình Trạng', width: 110, dataType: 'string' }, // Q
      { dataField: 'productCode', caption: 'Nhóm Hàng', width: 130, dataType: 'string' }, // R

      // ===== Cột phụ trợ =====
      {
        dataField: 'statusExport',
        caption: 'Trạng thái giá',
        width: 130,
        alignment: 'center',
        dataType: 'string',
        cellRender: (props: any) => <StatusPriceBadge data={props.data} />
      },
      { dataField: 'updateUserExport', caption: 'Người cập nhật', width: 130, dataType: 'string' },
      {
        dataField: 'lastImportDateExport',
        caption: 'Ngày import',
        minWidth: 150,
        dataType: 'string',
        calculateSortValue: (row: any) => row._importTs
      } as any
    ],
    [formatCustomSum, handleEdit, handleDelete]
  );

  /* ---------------------------------------------------------------------- */
  /* Render                                                                 */
  /* ---------------------------------------------------------------------- */

  const renderTable = useMemo(
    () => (
      <Table
        key={`trahang-${checkAuth ? 'auth' : 'noauth'}`}
        tableName="Trả hàng"
        tableId="trahang"
        keyExpr="key"
        columns={columns}
        isStriped={true}
        data={dataTable}
        wrapTextData={false}
        fileNameExport="Tra hang"
        hiddenAddButton
        isHiddenToolbar={false}
        oncClickReload={callBackRefresh}
        reload
        noHiddenPaging
        allowExcel
        enableLoadingPanel={false}
        setOpenImportXls={setOpenImportXls}
        showImportExcel={checkAuth}
        minPageSize={MINPAGESIZE3}
        downHeight={11.2}
        onInitialized={onGridInitialized}
        customCenterToolbar={() => <TableTitle title="Trả hàng" />}
      />
    ),
    [columns, dataTable, callBackRefresh, checkAuth, onGridInitialized]
  );

  const batchOptions = useMemo(
    () =>
      batches.map((b: any) => ({
        value: b.id,
        label: `${String(b.month).padStart(2, '0')}/${b.year} - ${b.importUser} - ${
          b.importDate ? moment(b.importDate).format('DD/MM/YYYY HH:mm') : ''
        }`
      })),
    [batches]
  );

  const headerPage = useMemo(
    () => (
      <div className="flex lg:h-fit px-2 items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Tháng:</span>
          <DatePicker
            picker="month"
            allowClear={false}
            format="MM/YYYY"
            value={moment(`${query.year}-${query.month}`, 'YYYY-M')}
            locale={viVN.DatePicker}
            // Chỉ đổi query để lọc lại danh sách file - KHÔNG gọi API
            onChange={(value: any) => value && setQuery({ year: value.year(), month: value.month() + 1 })}
          />
        </div>

        {/* <div className="flex items-center gap-2">
          <span className="text-sm font-medium">File import:</span>
          <Select
            style={{ minWidth: 260 }}
            value={pendingBatchId}
            placeholder="Chưa có file import"
            onChange={(value: number) => setPendingBatchId(value)}
            options={batchOptions}
          />
        </div> */}

        <Button type="primary" onClick={onClickViewBatch}>
          Xem
        </Button>
      </div>
    ),
    [query.year, query.month, pendingBatchId, batchOptions, onClickViewBatch]
  );

  return (
    <Layout path={[{ name: 'Trả hàng' }]}>
      {isUpdate && (
        <UpdatePriceReturn
          isOpen={isUpdate}
          rowSelect={selectedRows}
          allRows={dataTable}
          callBackClose={() => setIsUpdate(false)}
          callBackRefresh={callBackRefresh}
        />
      )}

      {isDelete && (
        <DeleteReturnProduct
          isOpen={isDelete}
          rowSelect={selectedRows}
          callBackClose={() => setIsDelete(false)}
          callBackRefresh={callBackRefresh}
        />
      )}

      {openImportXls && (
        <ImportFileModal
          isOpen={openImportXls}
          year={query.year}
          month={query.month}
          callBackClose={() => setOpenImportXls(false)}
          callBackRefresh={callBackRefresh}
        />
      )}

      <div className="relative">
        {headerPage}
        <div className="mt-2">{renderTable}</div>
      </div>
    </Layout>
  );
}
